import { useEffect, useState } from "react";
import { Phone, Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link, NavLink, useLocation } from "react-router-dom";

const NAV_LINKS = [
  { to: "/", label: "Accueil", end: true },
  { to: "/demenagement-particulier", label: "Particulier" },
  { to: "/entreprise", label: "Entreprise" },
  { to: "/garde-meuble", label: "Garde-meuble" },
  { to: "/monte-meuble", label: "Monte-meuble" },
  { to: "/formules", label: "Formules" },
  { to: "/calculer-mon-volume", label: "Calculateur" },
  { to: "/faq", label: "FAQ" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const linkClasses = ({ isActive }) =>
    `text-[13px] xl:text-sm font-medium transition-colors ${
      isActive ? "text-[#237cb5]" : "text-[#0f1d2b]/80 hover:text-[#237cb5]"
    }`;

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-[transform,box-shadow,border-color,background-color,color] duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-md border-b border-[#e6edf3] shadow-[0_8px_30px_-18px_rgba(15,29,43,0.25)]"
          : "bg-white/70 backdrop-blur-sm"
      }`}
    >
      <div className="bd-container flex items-center justify-between h-16 sm:h-20 gap-4">
        <Link
          to="/"
          className="flex items-center shrink-0"
          data-testid="logo-link"
          aria-label="Big Déménagement - Accueil"
        >
          <picture>
            <source
              media="(min-width: 640px)"
              srcSet="/big-demenagement-logo-desktop.webp"
            />
            <img
              src="/big-demenagement-logo-mobile.webp"
              alt="Logo Big Déménagement — société de déménagement en Île-de-France"
              width="260"
              height="92"
              className="h-10 sm:h-12 w-auto"
              loading="eager"
              decoding="async"
            />
          </picture>
        </Link>

        <nav className="hidden xl:flex items-center gap-6 2xl:gap-7">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={linkClasses}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          <a
            href="tel:+33185117279"
            data-testid="header-phone-btn"
            className="hidden md:inline-flex items-center gap-2 text-sm font-semibold text-[#237cb5] hover:text-[#143f5d] transition-colors"
          >
            <Phone className="h-4 w-4" aria-hidden="true" />
            01 85 11 72 79
          </a>
          <Button
            asChild
            data-testid="header-cta-devis"
            className="inline-flex bg-[#237cb5] hover:bg-[#1c6699] text-white rounded-xl px-4 sm:px-5 h-9 sm:h-10 text-[13px] sm:text-sm font-semibold"
          >
            <Link to="/demande-de-devis">Devis gratuit</Link>
          </Button>
          <button
            type="button"
            aria-label={open ? "Fermer le menu" : "Ouvrir le menu"}
            data-testid="mobile-menu-toggle"
            className="xl:hidden p-2 rounded-lg border border-[#e6edf3] bg-white text-[#0f1d2b]"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div
          className="xl:hidden bg-white border-t-2 border-b-4 border-t-[#e6edf3] border-b-[#237cb5] shadow-[0_24px_40px_-16px_rgba(15,29,43,0.25)]"
          data-testid="mobile-menu"
        >
          <div className="bd-container py-4 flex flex-col gap-1">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `py-2.5 text-[15px] font-medium border-b border-[#e6edf3] last:border-0 ${
                    isActive ? "text-[#237cb5]" : "text-[#0f1d2b]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}
